import { serialize } from 'object-to-formdata';
import { z } from 'zod';

import { CelebrationType, CelebrationVisibility, CelebrationWorkAnniversaryPointSetting } from '../../entities/celebrations';
import { CreateCelebrationAPIData, UpdateCelebrationAPIData } from '../../queries/celebrations';
import { toTimelessDateString } from '../../utils/date';
import { CelebrationFormData } from './types';

const buildRuleConditions = (attr: string, values: string[]) => values.filter(Boolean).map((value) => ({ attr, value }));

const buildFormBody = (data: CelebrationFormData) => {
	let amount = data.amount;
	let yearAmountMappings = data.yearAmountMappings;

	if (data.type === CelebrationType.WorkAnniversary && data.workAnniversaryPointSetting === CelebrationWorkAnniversaryPointSetting.OnlySpecificYears) {
		amount = null;
	} else if (
		data.type === CelebrationType.WorkAnniversary &&
		data.workAnniversaryPointSetting === CelebrationWorkAnniversaryPointSetting.EveryYearWithSameAmount
	) {
		yearAmountMappings = [];
	}

	return {
		amount: amount,
		celebrationDate: toTimelessDateString(data.celebrationDate),
		headline: data.headline.trim(),
		name: data.name.trim(),
		reason: data.reason.trim(),
		visibility: data.visibility,
		yearAmountMappings: yearAmountMappings,
		receivingRulesConditions: [
			buildRuleConditions('department', data.recipientDepartments),
			buildRuleConditions('location', data.recipientLocations),
			buildRuleConditions('manager_id', data.recipientTeams),
		].flat(),
	};
};

const attachFileData = <T extends { icon?: File | null; image?: File | null }>(body: T, data: CelebrationFormData): T | FormData => {
	let sendAsFormData = false;

	if (data.icon instanceof File) {
		body.icon = data.icon;
		sendAsFormData = true;
	} else if (!data.icon) {
		body.icon = null;
	}

	if (data.image instanceof File) {
		body.image = data.image;
		sendAsFormData = true;
	} else if (!data.image) {
		body.image = null;
	}

	if (sendAsFormData) {
		const snakeCasedBody = buildSnakeCasedBody(body);
		return serialize(snakeCasedBody);
	}

	return body;
};

const buildSnakeCasedBody = (body: Record<string, any>) => {
	const snakeCasedBody: Record<string, any> = {};
	for (const key in body) {
		const snakeCaseKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();

		if (body[key] instanceof Array) {
			snakeCasedBody[snakeCaseKey] = body[key].map((item: any) => buildSnakeCasedBody(item));
		} else if (body[key] instanceof File) {
			snakeCasedBody[snakeCaseKey] = body[key];
		} else if (body[key] instanceof Object) {
			snakeCasedBody[snakeCaseKey] = buildSnakeCasedBody(body[key]);
		} else {
			snakeCasedBody[snakeCaseKey] = body[key];
		}
	}
	return snakeCasedBody;
};

export const buildCreateFormBody = (data: CelebrationFormData): CreateCelebrationAPIData | FormData => {
	const body: CreateCelebrationAPIData = {
		type: data.type,
		...buildFormBody(data),
	};

	return attachFileData(body, data);
};

export const buildUpdateFormBody = (data: CelebrationFormData): UpdateCelebrationAPIData | FormData => {
	const body: UpdateCelebrationAPIData = {
		// Whenever we edit a celebration we reactivate it
		state: 'active',
		...buildFormBody(data),
	};

	return attachFileData(body, data);
};

export const validAmount = (reason: string) => {
	return /(^|\s)\+amount($|\s)/.test(reason);
};

export const createSchema = ({ forceScope }: { forceScope?: string } = {}) => {
	const schema = z
		.object({
			amount: z.coerce.number().nonnegative(),
			celebrationDate: z.date().nullable(),
			icon: z.any().nullable(),
			image: z.any().nullable(),
			headline: z.string().trim().min(1).max(40),
			name: z.string().trim().min(1).max(40),
			reason: z.string().trim().min(1).max(120),
			recipientDepartments: z.array(z.string().optional()),
			recipientLocations: z.array(z.string().optional()),
			workAnniversaryPointSetting: z.nativeEnum(CelebrationWorkAnniversaryPointSetting),
			recipientTeams: z.array(z.string().optional()),
			type: z.nativeEnum(CelebrationType),
			visibility: z.nativeEnum(CelebrationVisibility),
			yearAmountMappings: z.array(
				z.object({
					yearThreshold: z.coerce.number().positive(),
					amount: z.coerce.number().positive(),
				})
			),
		})
		.refine((data) => data.type !== CelebrationType.Custom || data.celebrationDate, {
			message: 'Date is required',
			path: ['celebrationDate'],
		})
		.refine((data) => data.type !== CelebrationType.Custom || data.icon, {
			message: 'Icon is required',
			path: ['icon'],
		})
		.refine((data) => data.type !== CelebrationType.Custom || data.image, {
			message: 'Image is required',
			path: ['image'],
		})
		.refine((data) => data.reason.includes('@recipient'), {
			message: 'reason must contain "@recipient"',
			path: ['reason'],
		})
		.refine((data) => (data.reason.match(/@recipient/g) ?? []).length <= 1, {
			message: 'may only contain one "@recipient"',
			path: ['reason'],
		})
		.refine(
			(data) => {
				if (
					data.type === CelebrationType.WorkAnniversary &&
					data.workAnniversaryPointSetting !== CelebrationWorkAnniversaryPointSetting.EveryYearWithSameAmount
				) {
					return validAmount(data.reason);
				} else {
					return validAmount(data.reason) || !data.amount;
				}
			},
			{
				message: 'reason must contain "+amount"',
				path: ['reason'],
			}
		)
		.refine((data) => (data.reason.match(/\+amount/g) ?? []).length <= 1, {
			message: 'may only contain one "+amount"',
			path: ['reason'],
		})
		.refine((data) => !/\+\d/.test(data.reason), {
			message: 'amount must not be hardcoded',
			path: ['reason'],
		})
		.refine((data) => forceScope !== 'department' || (forceScope === 'department' && data.recipientDepartments.filter(Boolean).length > 0), {
			message: 'recipientDepartments must be provided',
			path: ['recipientDepartments'],
		})
		.refine((data) => forceScope !== 'location' || (forceScope === 'location' && data.recipientLocations.filter(Boolean).length > 0), {
			message: 'recipientLocations must be provided',
			path: ['recipientLocations'],
		})
		.refine((data) => forceScope !== 'team' || (forceScope === 'team' && data.recipientTeams.filter(Boolean).length > 0), {
			message: 'recipientTeams must be provided',
			path: ['recipientTeams'],
		});

	return schema;
};
